import React, { useState, useEffect } from "react";
import "./Statistics.css";
import { userService, projectService } from "../services/api";
import api from "../services/api";

const StatisticsImproved = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

  const [statistics, setStatistics] = useState({
    overview: {
      totalRevenue: 0,
      totalCosts: 0,
      totalProfit: 0,
      profitMargin: 0,
      totalEmployees: 0,
      totalProjects: 0,
      totalWorkHours: 0,
      avgHourlyProfit: 0,
      avgDailyProfit: 0,
      avgProfitPerEmployee: 0,
      avgProfitPerProject: 0,
      roi: 0,
      costPerHour: 0,
      revenuePerHour: 0,
      weekdayHours: 0,
      weekendHours: 0,
      holidayHours: 0,
      totalSalaries: 0,
      totalAccommodation: 0,
      profitability: 0,
      efficiency: 0,
    },
    previousOverview: {
      totalRevenue: 0,
      totalCosts: 0,
      totalProfit: 0,
      profitMargin: 0,
    },
    owners: {
      miroslav: { share: 0, name: "Miroslav", percentage: 50 },
      dominik: { share: 0, name: "Dominik", percentage: 50 },
    },
    employees: [],
    projects: [],
    topPerformers: [],
    worstPerformers: [],
    monthlyStats: [],
  });

  // Owner transactions state
  const [ownerTransactions, setOwnerTransactions] = useState([]);
  const [showWithdrawalForm, setShowWithdrawalForm] = useState({
    miroslav: false,
    dominik: false,
  });
  const [showExpenseForm, setShowExpenseForm] = useState({
    miroslav: false,
    dominik: false,
  });
  const [transactionForm, setTransactionForm] = useState({
    amount: "",
    note: "",
    date: new Date().toISOString().split("T")[0],
  });
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  // Database maintenance state
  const [duplicatesInfo, setDuplicatesInfo] = useState(null);
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);

  // Employee detail modal state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeDetailModal, setShowEmployeeDetailModal] = useState(false);
  const [employeeMonthlyDetails, setEmployeeMonthlyDetails] = useState([]);

  // Helper: Bezpečné parsovanie dátumu (lokálny čas) pre komponent
  const parseDateLocal = (dateStr) => {
    if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split("-").map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date(dateStr);
  };

  const getRotationHours = (rotation, project, weekend) => {
    const importedHours = parseFloat(rotation.hours_worked);
    if (Number.isFinite(importedHours)) return importedHours;
    return weekend
      ? parseFloat(project.weekend_hours) || 0
      : parseFloat(project.weekday_hours) || 0;
  };

  const getBillingRateForRotation = (employee, project, rotation, weekend) => {
    const history = Array.isArray(employee.billing_rate_history) ? employee.billing_rate_history : [];
    const rateForDate = history
      .filter((rate) => rate.effective_from <= rotation.date)
      .sort((a, b) => a.effective_from.localeCompare(b.effective_from) || a.id - b.id)
      .pop();

    if (rateForDate) {
      const premiumShift = weekend || rotation.position === "night";
      return premiumShift
        ? parseFloat(rateForDate.weekend_night_rate) || 0
        : parseFloat(rateForDate.weekday_rate) || 0;
    }

    return weekend
      ? parseFloat(project.weekend_rate) || 45
      : parseFloat(project.weekday_rate) || 40;
  };


  // Previous period helper
  const getPreviousFilter = (filter) => {
    const prev = { ...filter };
    // YoY comparison: same month, previous year; for whole year, previous year
    if (filter.month === "all") {
      prev.year = parseInt(filter.year, 10) - 1;
      prev.month = "all";
      return prev;
    }
    // month-specific: keep same month, subtract one year
    prev.year = parseInt(filter.year, 10) - 1;
    prev.month = filter.month;
    return prev;
  };

  // 📊 NAČÍTANIE DÁT Z API
  const loadStatistics = async () => {
    try {
      setLoading(true);

      // Use api.js services for all API calls
      const [users, projects, rotations, ownerTransactionsData] =
        await Promise.all([
          userService.getAll(),
          projectService.getAll(),
          api.get("/project-rotations").then((res) => res.data),
          api
            .get("/owner-transactions")
            .then((res) => res.data)
            .catch(() => []),
        ]);

      setOwnerTransactions(ownerTransactionsData);

      console.log("📊 Loaded data:", {
        users: users.length,
        projects: projects.length,
        rotations: rotations.length,
        ownerTransactions: ownerTransactionsData.length,
      });

      const processedStats = calculateStatistics(
        users,
        projects,
        rotations,
        dateFilter,
      );
      const prevFilter = getPreviousFilter(dateFilter);
      const prevStats = calculateStatistics(
        users,
        projects,
        rotations,
        prevFilter,
      );
      setStatistics({
        ...processedStats,
        previousOverview: prevStats.overview,
        prevFilter,
      });
    } catch (error) {
      console.error("❌ Error loading statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  // 🔢 VÝPOČET ŠTATISTÍK Z PROJECT_ROTATIONS
  const calculateStatistics = (users, projects, rotations, filter) => {
    console.log("🔢 Calculating statistics from project_rotations...");

    // Helper: Bezpečné parsovanie dátumu (lokálny čas)
    const parseDateLocal = (dateStr) => {
      if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split("-").map(Number);
        return new Date(y, m - 1, d);
      }
      return new Date(dateStr);
    };

    // Helper: Zisti, či je deň víkend
    const isWeekend = (dateStr) => {
      const date = parseDateLocal(dateStr);
      const day = date.getDay();
      return day === 0 || day === 6; // Nedeľa alebo Sobota
    };

    const getRotationHours = (rotation, project, weekend) => {
      const importedHours = parseFloat(rotation.hours_worked);
      if (Number.isFinite(importedHours)) return importedHours;
      return weekend
        ? parseFloat(project.weekend_hours) || 0
        : parseFloat(project.weekday_hours) || 0;
    };

    const getBillingRateForRotation = (employee, project, rotation, weekend) => {
      const history = Array.isArray(employee.billing_rate_history) ? employee.billing_rate_history : [];
      const rateForDate = history
        .filter((rate) => rate.effective_from <= rotation.date)
        .sort((a, b) => a.effective_from.localeCompare(b.effective_from) || a.id - b.id)
        .pop();

      if (rateForDate) {
        const premiumShift = weekend || rotation.position === "night";
        return premiumShift
          ? parseFloat(rateForDate.weekend_night_rate) || 0
          : parseFloat(rateForDate.weekday_rate) || 0;
      }

      return weekend
        ? parseFloat(project.weekend_rate) || 45
        : parseFloat(project.weekday_rate) || 40;
    };


    // Helper: Zisti mesiac z dátumu
    const getMonth = (dateStr) => {
      const date = parseDateLocal(dateStr);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    };

    // Filtrovanie rotácií podľa dátumu a shift='work'
    const filteredYear = parseInt(filter.year, 10);
    const filteredMonth =
      filter.month === "all" ? "all" : parseInt(filter.month, 10);

    // Dátumový limit: ak je vybraný aktuálny rok, započítaj len dni do včera (exclude dnešok a budúcnosť)
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);

    const filteredRotations = rotations.filter((r) => {
      const date = parseDateLocal(r.date);
      // Filtruj len work rotácie (nie home)
      if (r.shift !== "work") return false;

      // Striktne podľa roka
      if (date.getFullYear() !== filteredYear) return false;

      // Pre aktuálny rok berieme len dáta do včera
      if (filteredYear === now.getFullYear() && date > yesterday) return false;

      // Ak je vybraný mesiac, filtruj aj podľa mesiaca
      if (filteredMonth && filteredMonth !== "all") {
        return date.getMonth() + 1 === filteredMonth;
      }
      return true;
    });

    console.log(
      `📊 Filtered ${filteredRotations.length} work rotations for ${filter.year}/${filter.month}`,
    );

    // ==============================================
    // 1️⃣ ŠTATISTIKY PRE ZAMESTNANCOV
    // ==============================================
    // OPRAVA: Zahrnúť všetkých používateľov (employee, admin, supervisor, manager)
    // Každý kto pracuje by mal byť zahrnutý do štatistík
    const employeeStats = users
      .map((employee) => {
        const employeeRotations = filteredRotations.filter(
          (r) => r.user_id === employee.id,
        );

        let totalHours = 0;
        let totalSalaryCost = 0;
        let totalRevenue = 0;
        let weekdayHours = 0;
        let weekendHours = 0;

        employeeRotations.forEach((rotation) => {
          const project = projects.find((p) => p.id === rotation.project_id);
          if (!project) return;

          const weekend = isWeekend(rotation.date);
          const hours = getRotationHours(rotation, project, weekend);

          totalHours += hours;

          if (weekend) {
            weekendHours += hours;
            // Mzda zamestnanca
            const empRate =
              parseFloat(employee.hourly_rate_weekend) ||
              parseFloat(employee.weekend_rate) ||
              29;
            totalSalaryCost += hours * empRate;
            // Príjem firmy
            const projRate = getBillingRateForRotation(employee, project, rotation, weekend);
            totalRevenue += hours * projRate;
          } else {
            weekdayHours += hours;
            // Mzda zamestnanca
            const empRate =
              parseFloat(employee.hourly_rate_weekday) ||
              parseFloat(employee.hourly_rate) ||
              29;
            totalSalaryCost += hours * empRate;
            // Príjem firmy
            const projRate = getBillingRateForRotation(employee, project, rotation, weekend);
            totalRevenue += hours * projRate;
          }
        });

        // Náklady na ubytovanie - PAUŠÁLNA MESAČNÁ CENA ROZDELENÁ NA ĽUDÍ
        // Ubytovanie sa delí na počet ľudí s accommodation='company' na projekte
        let accommodationCost = 0;
        if (employee.accommodation === "company") {
          // Zisti unikátne projekty a mesiace kde pracoval
          const uniqueProjectMonths = new Set();
          employeeRotations.forEach((r) => {
            const monthKey = `${r.project_id}-${getMonth(r.date)}`;
            uniqueProjectMonths.add(monthKey);
          });

          // Pre každý projekt-mesiac rozdeliť cenu medzi ľudí
          uniqueProjectMonths.forEach((key) => {
            // key má tvar `${project_id}-${YYYY-MM}` -> rozdeľ presne na prvú pomlčku
            const sepIdx = key.indexOf("-");
            const projectId = key.slice(0, sepIdx);
            const monthKey = key.slice(sepIdx + 1); // YYYY-MM
            const project = projects.find((p) => p.id === parseInt(projectId));
            if (project && project.accommodation_price_monthly) {
              // Spočítaj koľko ľudí s accommodation='company' pracovalo na tomto projekte v tomto mesiaci
              const employeesWithAccommodation = new Set();
              filteredRotations.forEach((r) => {
                if (
                  r.project_id === parseInt(projectId) &&
                  getMonth(r.date) === monthKey
                ) {
                  const emp = users.find((u) => u.id === r.user_id);
                  if (emp && emp.accommodation === "company") {
                    employeesWithAccommodation.add(r.user_id);
                  }
                }
              });

              // Rozdeliť mesačnú cenu na počet ľudí
              const employeeCount = employeesWithAccommodation.size;
              if (employeeCount > 0) {
                accommodationCost +=
                  parseFloat(project.accommodation_price_monthly) /
                  employeeCount;
              }
            }
          });
        }

        // Zisk = príjem - (mzda + ubytovanie)
        const totalCost = totalSalaryCost + accommodationCost;
        const profit = totalRevenue - totalCost;
        const profitMargin =
          totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0;
        const profitPerHour = totalHours > 0 ? profit / totalHours : 0;
        const avgHoursPerDay =
          employeeRotations.length > 0
            ? totalHours / employeeRotations.length
            : 0;

        return {
          id: employee.id,
          name: employee.full_name || employee.username,
          totalHours,
          totalDays: employeeRotations.length,
          weekdayHours,
          weekendHours,
          totalSalaryCost,
          accommodationCost,
          totalCost,
          totalRevenue,
          profit,
          profitMargin,
          profitPerHour,
          avgHoursPerDay,
          accommodation: employee.accommodation || "self",
          role: employee.role,
          blocked:
            employee.blocked === 1 ||
            employee.blocked === true ||
            employee.blocked === "1",
        };
      })
      .filter((emp) => emp.totalHours > 0) // Zobraz iba tých, ktorí skutočne pracovali
      .sort((a, b) => b.profit - a.profit);

    // ==============================================
    // 2️⃣ ŠTATISTIKY PRE PROJEKTY
    // ==============================================
    const projectStats = projects
      .map((project) => {
        const projectRotations = filteredRotations.filter(
          (r) => r.project_id === project.id,
        );

        let totalHours = 0;
        let totalSalaryCost = 0;
        let totalRevenue = 0;
        let weekdayHours = 0;
        let weekendHours = 0;

        projectRotations.forEach((rotation) => {
          const employee = users.find((u) => u.id === rotation.user_id);
          if (!employee) return;

          const weekend = isWeekend(rotation.date);
          const hours = getRotationHours(rotation, project, weekend);

          totalHours += hours;

          if (weekend) {
            weekendHours += hours;
            // Mzda zamestnanca
            const empRate =
              parseFloat(employee.hourly_rate_weekend) ||
              parseFloat(employee.weekend_rate) ||
              29;
            totalSalaryCost += hours * empRate;
            // Príjem firmy
            const projRate = getBillingRateForRotation(employee, project, rotation, weekend);
            totalRevenue += hours * projRate;
          } else {
            weekdayHours += hours;
            // Mzda zamestnanca
            const empRate =
              parseFloat(employee.hourly_rate_weekday) ||
              parseFloat(employee.hourly_rate) ||
              29;
            totalSalaryCost += hours * empRate;
            // Príjem firmy
            const projRate = getBillingRateForRotation(employee, project, rotation, weekend);
            totalRevenue += hours * projRate;
          }
        });

        // Náklady na ubytovanie - PAUŠÁLNA MESAČNÁ CENA ROZDELENÁ NA ĽUDÍ
        // Cena sa delí na počet ľudí s accommodation='company', ale celková suma zostáva rovnaká
        let totalAccommodationCost = 0;
        if (project.accommodation_price_monthly) {
          // Zisti unikátne mesiace
          const uniqueMonths = [
            ...new Set(projectRotations.map((r) => getMonth(r.date))),
          ];

          // Pre každý mesiac spočítaj ľudí a celkovú cenu (accommodation_price_monthly)
          uniqueMonths.forEach((month) => {
            const monthRotations = projectRotations.filter(
              (r) => getMonth(r.date) === month,
            );

            // Zisti unikátnych ľudí s ubytovaním
            const employeesWithAccommodation = new Set();
            monthRotations.forEach((r) => {
              const emp = users.find((u) => u.id === r.user_id);
              if (emp && emp.accommodation === "company") {
                employeesWithAccommodation.add(r.user_id);
              }
            });

            // Ak sú ľudia s ubytovaním, platí sa celá mesačná cena
            if (employeesWithAccommodation.size > 0) {
              totalAccommodationCost += parseFloat(
                project.accommodation_price_monthly,
              );
            }
          });
        }

        const totalCost = totalSalaryCost + totalAccommodationCost;
        const profit = totalRevenue - totalCost;
        const profitMargin =
          totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0;
        const profitPerHour = totalHours > 0 ? profit / totalHours : 0;
        const uniqueEmployees = [
          ...new Set(projectRotations.map((r) => r.user_id)),
        ].length;
        const avgHoursPerDay =
          projectRotations.length > 0
            ? totalHours / projectRotations.length
            : 0;

        return {
          id: project.id,
          name: project.name,
          totalHours,
          totalDays: projectRotations.length,
          weekdayHours,
          weekendHours,
          totalSalaryCost,
          accommodationCost: totalAccommodationCost,
          totalCost,
          totalRevenue,
          profit,
          profitMargin,
          profitPerHour,
          avgHoursPerDay,
          uniqueEmployees,
          status: project.status,
        };
      })
      .sort((a, b) => b.profit - a.profit);

    // ==============================================
    // 3️⃣ MESAČNÉ ŠTATISTIKY (PRE GRAFY)
    // ==============================================
    const monthlyStats = [];
    const allMonths = [
      ...new Set(filteredRotations.map((r) => getMonth(r.date))),
    ].sort();

    allMonths.forEach((month) => {
      const monthRotations = filteredRotations.filter(
        (r) => getMonth(r.date) === month,
      );

      let totalRevenue = 0;
      let totalSalaries = 0;
      let totalAccommodation = 0;
      let totalHours = 0;

      monthRotations.forEach((rotation) => {
        const project = projects.find((p) => p.id === rotation.project_id);
        const employee = users.find((u) => u.id === rotation.user_id);
        if (!project || !employee) return;

        const weekend = isWeekend(rotation.date);
        const hours = getRotationHours(rotation, project, weekend);

        totalHours += hours;

        if (weekend) {
          const empRate =
            parseFloat(employee.hourly_rate_weekend) ||
            parseFloat(employee.weekend_rate) ||
            29;
          totalSalaries += hours * empRate;
          const projRate = getBillingRateForRotation(employee, project, rotation, weekend);
          totalRevenue += hours * projRate;
        } else {
          const empRate =
            parseFloat(employee.hourly_rate_weekday) ||
            parseFloat(employee.hourly_rate) ||
            29;
          totalSalaries += hours * empRate;
          const projRate = getBillingRateForRotation(employee, project, rotation, weekend);
          totalRevenue += hours * projRate;
        }
      });

      // Ubytovanie - PAUŠÁLNA MESAČNÁ CENA ZA PROJEKT
      // Každý projekt kde pracoval aspoň 1 zamestnanec s accommodation='company' = jedna mesačná cena
      projects.forEach((project) => {
        if (project.accommodation_price_monthly) {
          const projectMonthRotations = monthRotations.filter(
            (r) => r.project_id === project.id,
          );

          // Ak aspoň 1 zamestnanec má accommodation='company', platí sa celá mesačná cena
          const hasAccommodation = projectMonthRotations.some((r) => {
            const emp = users.find((u) => u.id === r.user_id);
            return emp && emp.accommodation === "company";
          });

          if (hasAccommodation) {
            totalAccommodation += parseFloat(
              project.accommodation_price_monthly,
            );
          }
        }
      });

      const totalCosts = totalSalaries + totalAccommodation;
      const profit = totalRevenue - totalCosts;
      const profitMargin = totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0;

      monthlyStats.push({
        month,
        totalRevenue,
        totalSalaries,
        totalAccommodation,
        totalCosts,
        profit,
        profitMargin,
        totalHours,
      });
    });

    // ==============================================
    // 4️⃣ CELKOVÝ PREHĽAD
    // ==============================================
    const totalRevenue = employeeStats.reduce(
      (sum, e) => sum + e.totalRevenue,
      0,
    );
    const totalSalaries = employeeStats.reduce(
      (sum, e) => sum + e.totalSalaryCost,
      0,
    );

    // OPRAVA: Ubytovanie počítame z monthlyStats (správne zahŕňa VŠETKÝCH users vrátane adminov)
    // Nie z employeeStats (ktorý filtruje len role='employee' a vynecháva adminov s accommodation='company')
    const totalAccommodation = monthlyStats.reduce(
      (sum, m) => sum + m.totalAccommodation,
      0,
    );

    const totalCosts = totalSalaries + totalAccommodation;
    const totalProfit = totalRevenue - totalCosts;
    const profitMargin =
      totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
    const totalWorkHours = employeeStats.reduce(
      (sum, e) => sum + e.totalHours,
      0,
    );
    const weekdayHours = employeeStats.reduce(
      (sum, e) => sum + e.weekdayHours,
      0,
    );
    const weekendHours = employeeStats.reduce(
      (sum, e) => sum + e.weekendHours,
      0,
    );
    const totalEmployees = employeeStats.filter((e) => e.totalHours > 0).length;
    const totalProjects = projectStats.filter((p) => p.totalHours > 0).length;

    const avgHourlyProfit =
      totalWorkHours > 0 ? totalProfit / totalWorkHours : 0;
    const avgDailyProfit =
      filteredRotations.length > 0 ? totalProfit / filteredRotations.length : 0;
    const avgProfitPerEmployee =
      totalEmployees > 0 ? totalProfit / totalEmployees : 0;
    const avgProfitPerProject =
      totalProjects > 0 ? totalProfit / totalProjects : 0;
    const roi = totalCosts > 0 ? (totalProfit / totalCosts) * 100 : 0;
    const costPerHour = totalWorkHours > 0 ? totalCosts / totalWorkHours : 0;
    const revenuePerHour =
      totalWorkHours > 0 ? totalRevenue / totalWorkHours : 0;
    const efficiency = totalCosts > 0 ? totalRevenue / totalCosts : 0;

    // Top/worst performers
    const topPerformers = employeeStats
      .filter((e) => e.totalHours > 0 && !e.blocked)
      .slice(0, 5);
    const worstPerformers = employeeStats
      .filter((e) => e.totalHours > 0 && !e.blocked)
      .slice(-5)
      .reverse();

    // Rozdelenie zisku pre majiteľov
    const owners = {
      miroslav: {
        share: totalProfit * 0.5,
        name: "Miroslav",
        percentage: 50,
      },
      dominik: {
        share: totalProfit * 0.5,
        name: "Dominik",
        percentage: 50,
      },
    };

    return {
      overview: {
        totalRevenue,
        totalCosts,
        totalProfit,
        profitMargin,
        totalEmployees,
        totalProjects,
        totalWorkHours,
        avgHourlyProfit,
        avgDailyProfit,
        avgProfitPerEmployee,
        avgProfitPerProject,
        roi,
        costPerHour,
        revenuePerHour,
        weekdayHours,
        weekendHours,
        holidayHours: 0,
        totalSalaries,
        totalAccommodation,
        profitability: profitMargin,
        efficiency,
      },
      owners,
      employees: employeeStats.filter((e) => !e.blocked),
      projects: projectStats,
      topPerformers,
      worstPerformers,
      monthlyStats,
    };
  };

  // Owner transaction handlers
  const handleAddTransaction = async (ownerName, type) => {
    // Validation
    if (!transactionForm.amount || parseFloat(transactionForm.amount) <= 0) {
      alert("Zadajte platnú sumu");
      return;
    }
    if (!transactionForm.note || transactionForm.note.trim() === "") {
      alert("Zadajte poznámku");
      return;
    }

    try {
      const response = await api.post("/owner-transactions", {
        owner_name: ownerName,
        type: type,
        amount: parseFloat(transactionForm.amount),
        note: transactionForm.note,
        date: transactionForm.date,
      });

      if (response.data) {
        // Reset form
        setTransactionForm({
          amount: "",
          note: "",
          date: new Date().toISOString().split("T")[0],
        });

        // Close form
        if (type === "withdrawal") {
          setShowWithdrawalForm({ miroslav: false, dominik: false });
          setNotificationMessage("Výber prijatý");
        } else {
          setShowExpenseForm({ miroslav: false, dominik: false });
          setNotificationMessage("Platba prijatá");
        }

        // Show success notification
        setShowSuccessNotification(true);

        // Auto-refresh page after 3 seconds
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    } catch (error) {
      console.error("Error adding transaction:", error);
      alert(
        "Chyba pri pridávaní transakcie: " +
          (error.response?.data?.error || error.message),
      );
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm("Naozaj chcete vymazať túto transakciu?")) return;

    try {
      await api.delete(`/owner-transactions/${id}`);

      // Auto-refresh page to show updated data
      window.location.reload();
    } catch (error) {
      console.error("Error deleting transaction:", error);
      alert("Chyba pri mazaní transakcie");
    }
  };

  const handleToggleTransaction = (id) => {
    setSelectedTransactions((prev) => {
      if (prev.includes(id)) {
        return prev.filter((tid) => tid !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedTransactions.length === 0) {
      alert("Nie sú označené žiadne transakcie");
      return;
    }

    if (
      !window.confirm(
        `Naozaj chcete vymazať ${selectedTransactions.length} transakcií?`,
      )
    )
      return;

    try {
      await Promise.all(
        selectedTransactions.map((id) =>
          api.delete(`/owner-transactions/${id}`),
        ),
      );

      alert("Transakcie boli úspešne vymazané");

      // Auto-refresh page to show updated data
      window.location.reload();
    } catch (error) {
      console.error("Error deleting transactions:", error);
      alert("Chyba pri mazaní transakcií");
    }
  };

  // 🧹 DATABASE MAINTENANCE - Check for duplicates
  const checkForDuplicates = async () => {
    try {
      setCleaningDuplicates(true);
      const response = await api.get("/check-duplicate-rotations");
      setDuplicatesInfo(response.data);

      if (response.data.duplicatesFound === 0) {
        setNotificationMessage("✅ Žiadne duplikáty neboli nájdené");
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 3000);
      }
    } catch (error) {
      console.error("Error checking duplicates:", error);
      alert("Chyba pri kontrole duplikátov");
    } finally {
      setCleaningDuplicates(false);
    }
  };

  // 🧹 DATABASE MAINTENANCE - Clean duplicates
  const cleanDuplicates = async () => {
    if (
      !window.confirm(
        "Naozaj chcete vymazať všetky duplikáty z databázy? Táto akcia je nezvratná.",
      )
    ) {
      return;
    }

    try {
      setCleaningDuplicates(true);
      const response = await api.post("/clean-duplicate-rotations");

      setNotificationMessage(`✅ ${response.data.message}`);
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 5000);

      // Reload statistics after cleaning
      loadStatistics();

      // Clear duplicates info
      setDuplicatesInfo(null);
    } catch (error) {
      console.error("Error cleaning duplicates:", error);
      alert("Chyba pri čistení duplikátov");
    } finally {
      setCleaningDuplicates(false);
    }
  };

  // 👤 EMPLOYEE DETAIL MODAL
  const handleEmployeeClick = async (employee) => {
    try {
      setSelectedEmployee(employee);
      setShowEmployeeDetailModal(true);

      // Načítaj všetky rotácie pre tohto zamestnanca
      const rotationsResponse = await api.get("/project-rotations");
      const rotations = rotationsResponse.data;
      const users = await userService.getAll();
      const projects = await projectService.getAll();

      const employeeData = users.find((u) => u.id === employee.id);

      // Filtruj rotácie len pre aktuálny rok z dateFilter a pre aktuálny rok iba do včera
      const filteredYearForEmployee = parseInt(dateFilter.year, 10);
      const nowEmp = new Date();
      const todayEmp = new Date(
        nowEmp.getFullYear(),
        nowEmp.getMonth(),
        nowEmp.getDate(),
      );
      const yesterdayEmp = new Date(todayEmp.getTime() - 24 * 60 * 60 * 1000);
      const employeeRotations = rotations.filter((r) => {
        const rotationDate = new Date(r.date);
        if (r.user_id !== employee.id) return false;
        if (rotationDate.getFullYear() !== filteredYearForEmployee)
          return false;
        if (
          filteredYearForEmployee === nowEmp.getFullYear() &&
          rotationDate > yesterdayEmp
        )
          return false;
        return true;
      });

      // Zoskup rotácie podľa mesiacov
      const monthlyData = {};

      employeeRotations.forEach((rotation) => {
        const date = parseDateLocal(rotation.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        const monthName = date.toLocaleDateString("sk-SK", {
          year: "numeric",
          month: "long",
        });

        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {
            monthKey,
            monthName,
            workDays: 0,
            homeDays: 0,
            totalDays: 0,
            weekdayDays: 0,
            weekendDays: 0,
            totalHours: 0,
            weekdayHours: 0,
            weekendHours: 0,
            totalSalary: 0,
            totalRevenue: 0,
            projects: {},
          };
        }

        // Počítaj work a home dni
        if (rotation.shift === "work") {
          monthlyData[monthKey].workDays++;
        } else if (rotation.shift === "home") {
          monthlyData[monthKey].homeDays++;
        }

        // Počítaj hodiny a mzdy len pre work dni
        if (rotation.shift !== "work") return;

        const project = projects.find((p) => p.id === rotation.project_id);
        if (!project) return;

        const isWeekendDay = (dateStr) => {
          const d = parseDateLocal(dateStr);
          const day = d.getDay();
          return day === 0 || day === 6;
        };

        const weekend = isWeekendDay(rotation.date);
        const hours = getRotationHours(rotation, project, weekend);

        monthlyData[monthKey].totalDays++;
        if (weekend) {
          monthlyData[monthKey].weekendDays++;
          monthlyData[monthKey].weekendHours += hours;
          const empRate =
            parseFloat(employeeData.hourly_rate_weekend) ||
            parseFloat(employeeData.weekend_rate) ||
            29;
          const projRate = getBillingRateForRotation(employeeData, project, rotation, weekend);
          monthlyData[monthKey].totalSalary += hours * empRate;
          monthlyData[monthKey].totalRevenue += hours * projRate;
        } else {
          monthlyData[monthKey].weekdayDays++;
          monthlyData[monthKey].weekdayHours += hours;
          const empRate =
            parseFloat(employeeData.hourly_rate_weekday) ||
            parseFloat(employeeData.hourly_rate) ||
            29;
          const projRate = getBillingRateForRotation(employeeData, project, rotation, weekend);
          monthlyData[monthKey].totalSalary += hours * empRate;
          monthlyData[monthKey].totalRevenue += hours * projRate;
        }
        monthlyData[monthKey].totalHours += hours;

        // Zoskupiť podľa projektov
        if (!monthlyData[monthKey].projects[project.name]) {
          monthlyData[monthKey].projects[project.name] = {
            weekdayDays: 0,
            weekendDays: 0,
            weekdayHours: 0,
            weekendHours: 0,
            weekdayRate:
              parseFloat(employeeData.hourly_rate_weekday) ||
              parseFloat(employeeData.hourly_rate) ||
              29,
            weekendRate:
              parseFloat(employeeData.hourly_rate_weekend) ||
              parseFloat(employeeData.weekend_rate) ||
              29,
          };
        }

        if (weekend) {
          monthlyData[monthKey].projects[project.name].weekendDays++;
          monthlyData[monthKey].projects[project.name].weekendHours += hours;
        } else {
          monthlyData[monthKey].projects[project.name].weekdayDays++;
          monthlyData[monthKey].projects[project.name].weekdayHours += hours;
        }
      });

      // Preveď na array a zoraď podľa dátumu (najnovšie prvé)
      const monthlyArray = Object.values(monthlyData).sort((a, b) =>
        b.monthKey.localeCompare(a.monthKey),
      );
      setEmployeeMonthlyDetails(monthlyArray);
    } catch (error) {
      console.error("Error loading employee details:", error);
      alert("Chyba pri načítavaní detailov zamestnanca");
    }
  };

  // 🔄 RELOAD
  useEffect(() => {
    loadStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  // 📅 FILTER HANDLER
  const handleFilterChange = (field, value) => {
    setDateFilter((prev) => {
      if (field === "year") {
        // Pri zmene roka automaticky nastav "Celý rok"
        return { ...prev, year: parseInt(value, 10), month: "all" };
      }
      return { ...prev, [field]: value };
    });
  };

  // 🔒 BEZPEČNOSTNÁ KONTROLA
  if (!user || user.role !== "admin") {
    return (
      <div className="statistics-container">
        <div className="access-denied">
          <h2>🚫 Prístup zamietnutý</h2>
          <p>Štatistiky sú dostupné len pre administrátorov.</p>
          <p>Vaša rola: {user?.role || "Neprihlásený"}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="statistics-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Načítavam štatistiky...</p>
        </div>
      </div>
    );
  }

  const {
    overview,
    employees,
    projects: projectList,
    monthlyStats,
  } = statistics;

  // Aplikuj rovnaký dátový filter aj na firemné transakcie (rok/mesiac) + cutoff do včera v aktuálnom roku
  const selectedYear = parseInt(dateFilter.year, 10);
  const selectedMonth =
    dateFilter.month === "all" ? "all" : parseInt(dateFilter.month, 10);
  const nowGlobal = new Date();
  const todayGlobal = new Date(
    nowGlobal.getFullYear(),
    nowGlobal.getMonth(),
    nowGlobal.getDate(),
  );
  const yesterdayGlobal = new Date(todayGlobal.getTime() - 24 * 60 * 60 * 1000);
  const filteredOwnerTransactions = ownerTransactions.filter((t) => {
    const d = new Date(t.date);
    if (Number.isNaN(d.getTime())) return false;
    if (d.getFullYear() !== selectedYear) return false;
    if (selectedYear === nowGlobal.getFullYear() && d > yesterdayGlobal)
      return false;
    if (selectedMonth === "all") return true;
    return d.getMonth() + 1 === selectedMonth;
  });

  return (
    <div className="statistics-container">
      <div className="statistics-header">
        {/* NOVÉ ROZLOŽENIE: Názov vľavo, Filtre v strede, Tlačidlá vpravo pod sebou */}
        <div
          className="header-row"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          <div className="title-section" style={{ flex: "0 0 auto" }}>
            <h1>📈 Štatistiky</h1>
            <p>Komplexné štatistiky reálne dáta</p>
          </div>

          <div
            className="filter-section"
            style={{
              display: "flex",
              gap: "15px",
              flex: "1 1 auto",
              justifyContent: "center",
            }}
          >
            <div className="filter-group">
              <label>Rok:</label>
              <select
                value={dateFilter.year}
                onChange={(e) =>
                  handleFilterChange("year", parseInt(e.target.value))
                }
              >
                {[2028, 2027, 2026, 2025, 2024].map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Mesiac:</label>
              <select
                value={dateFilter.month}
                onChange={(e) => handleFilterChange("month", e.target.value)}
              >
                <option value="all">Celý rok</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => (
                  <option key={month} value={month}>
                    {new Date(2024, month - 1).toLocaleString("sk-SK", {
                      month: "long",
                    })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              flex: "0 0 auto",
              alignItems: "flex-end",
            }}
          >
            <button
              onClick={loadStatistics}
              className="refresh-btn"
              style={{ minWidth: "180px" }}
            >
              🔄 Obnoviť údaje
            </button>
            <button
              onClick={checkForDuplicates}
              className="refresh-btn"
              disabled={cleaningDuplicates}
              style={{ backgroundColor: "#ff9800", minWidth: "180px" }}
            >
              🔍 Kontrola duplikátov
            </button>
          </div>
        </div>
      </div>

      {/* SUCCESS NOTIFICATION */}
      {showSuccessNotification && (
        <div
          className="success-notification"
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            backgroundColor: "#4CAF50",
            color: "white",
            padding: "15px 25px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            zIndex: 1000,
            animation: "slideIn 0.3s ease-out",
          }}
        >
          {notificationMessage}
        </div>
      )}

      {/* DUPLICATES INFO PANEL */}
      {duplicatesInfo && duplicatesInfo.duplicatesFound > 0 && (
        <div
          className="duplicates-panel"
          style={{
            backgroundColor: "#fff3cd",
            border: "2px solid #ffc107",
            borderRadius: "8px",
            padding: "20px",
            marginBottom: "20px",
          }}
        >
          <h3 style={{ color: "#856404", marginTop: 0 }}>
            ⚠️ Nájdené duplikáty v databáze
          </h3>
          <p style={{ color: "#856404" }}>
            Počet skupín duplikátov:{" "}
            <strong>{duplicatesInfo.duplicatesFound}</strong>
          </p>

          {duplicatesInfo.details && duplicatesInfo.details.length > 0 && (
            <div
              style={{
                maxHeight: "200px",
                overflowY: "auto",
                marginTop: "10px",
              }}
            >
              <table style={{ width: "100%", fontSize: "14px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#ffc107" }}>
                    <th style={{ padding: "8px", textAlign: "left" }}>
                      Používateľ
                    </th>
                    <th style={{ padding: "8px", textAlign: "left" }}>
                      Projekt
                    </th>
                    <th style={{ padding: "8px", textAlign: "left" }}>Dátum</th>
                    <th style={{ padding: "8px", textAlign: "center" }}>
                      Počet
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {duplicatesInfo.details.map((dup, index) => (
                    <tr
                      key={index}
                      style={{
                        backgroundColor: index % 2 === 0 ? "#fff" : "#f8f9fa",
                      }}
                    >
                      <td style={{ padding: "8px" }}>{dup.user}</td>
                      <td style={{ padding: "8px" }}>{dup.project}</td>
                      <td style={{ padding: "8px" }}>{dup.date}</td>
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        {dup.count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: "15px", display: "flex", gap: "10px" }}>
            <button
              onClick={cleanDuplicates}
              disabled={cleaningDuplicates}
              style={{
                backgroundColor: "#dc3545",
                color: "white",
                padding: "10px 20px",
                border: "none",
                borderRadius: "5px",
                cursor: cleaningDuplicates ? "not-allowed" : "pointer",
                fontWeight: "bold",
              }}
            >
              {cleaningDuplicates ? "⏳ Čistenie..." : "🧹 Vyčistiť duplikáty"}
            </button>
            <button
              onClick={() => setDuplicatesInfo(null)}
              style={{
                backgroundColor: "#6c757d",
                color: "white",
                padding: "10px 20px",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              ✖️ Zavrieť
            </button>
          </div>
        </div>
      )}

      {/* MAIN CARDS */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Celkový počet používateľov</h3>
            <div className="stat-value">{overview.totalEmployees}</div>
            <div className="stat-meta">Aktívni: {overview.totalEmployees}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🏗️</div>
          <div className="stat-content">
            <h3>Projekty</h3>
            <div className="stat-value">{overview.totalProjects}</div>
            <div className="stat-meta">Aktívne: {overview.totalProjects}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <h3>Odpracované hodiny</h3>
            <div className="stat-value">
              {overview.totalWorkHours.toFixed(0)}
            </div>
            <div className="stat-meta">Pracovný čas</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🏖️</div>
          <div className="stat-content">
            <h3>Rotácie</h3>
            <div className="stat-value">
              {employees.reduce((sum, e) => sum + e.totalDays, 0)}
            </div>

            <div className="stat-meta">Pracovných dní</div>
          </div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>Celkové príjmy</h3>
            <div className="stat-value">
              €
              {overview.totalRevenue.toLocaleString("sk-SK", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </div>
            <div className="stat-meta">
              Zisk: €
              {overview.totalProfit.toLocaleString("sk-SK", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </div>
          </div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <h3>Zisková marža</h3>
            <div className="stat-value">
              {overview.profitMargin.toFixed(1)}%
            </div>
            <div className="stat-meta">ROI: {overview.roi.toFixed(1)}%</div>
          </div>
        </div>
      </div>

      {/* KPI Δ CARDS */}
      {statistics.previousOverview && (
        <div className="stats-grid" style={{ marginTop: "10px" }}>
          {(() => {
            const curr = overview;
            const prev = statistics.previousOverview;
            const kpis = [
              {
                key: "totalRevenue",
                label: "Príjmy",
                fmt: (v) => `€${v.toLocaleString("sk-SK")}`,
              },
              {
                key: "totalCosts",
                label: "Náklady",
                fmt: (v) => `€${v.toLocaleString("sk-SK")}`,
              },
              {
                key: "totalProfit",
                label: "Zisk",
                fmt: (v) => `€${v.toLocaleString("sk-SK")}`,
              },
              {
                key: "profitMargin",
                label: "Marža",
                fmt: (v) => `${v.toFixed(1)}%`,
              },
            ];
            return kpis.map((k) => {
              const currVal = curr[k.key] || 0;
              const prevVal = prev[k.key] || 0;
              const delta = currVal - prevVal;
              const pct = prevVal !== 0 ? (delta / prevVal) * 100 : null;
              const up = delta >= 0;
              return (
                <div
                  key={k.key}
                  className="stat-card"
                  style={{
                    borderLeft: "4px solid " + (up ? "#28a745" : "#dc3545"),
                  }}
                >
                  <div className="stat-content">
                    <h3>{k.label}</h3>
                    <div className="stat-value">{k.fmt(currVal)}</div>
                    <div
                      className="stat-meta"
                      style={{
                        color: up ? "#28a745" : "#dc3545",
                        fontWeight: "bold",
                      }}
                    >
                      {up ? "▲" : "▼"} {k.fmt(Math.abs(delta))}{" "}
                      {pct !== null
                        ? `(${up ? "+" : ""}${pct.toFixed(1)}%)`
                        : ""}
                    </div>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* TOP STRATY (projekty s negatívnym ziskom) */}
      {projectList.filter((p) => p.profit < 0).length > 0 && (
        <div className="losses-section" style={{ marginTop: "20px" }}>
          <h2>❗ Top straty (projekty)</h2>
          <div className="projects-table">
            <table>
              <thead>
                <tr>
                  <th>Projekt</th>
                  <th>Zisk</th>
                  <th>Príjmy</th>
                  <th>Náklady</th>
                  <th>Marža</th>
                </tr>
              </thead>
              <tbody>
                {projectList
                  .filter((p) => p.profit < 0)
                  .sort((a, b) => a.profit - b.profit)
                  .slice(0, 5)
                  .map((p) => (
                    <tr key={p.id}>
                      <td>
                        <strong>{p.name}</strong>
                      </td>
                      <td style={{ color: "#dc3545" }}>
                        €{p.profit.toLocaleString("sk-SK")}
                      </td>
                      <td>€{p.totalRevenue.toLocaleString("sk-SK")}</td>
                      <td>€{p.totalCost.toLocaleString("sk-SK")}</td>
                      <td>{p.profitMargin.toFixed(1)}%</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TREND (mesačný): stacked bar (náklady + zisk = príjem) */}
      {monthlyStats.length > 0 && (
        <div className="trend-section" style={{ marginTop: "20px" }}>
          <h2>📊 Trend príjmov a nákladov (mesačne)</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {(() => {
              const maxRev = Math.max(
                ...monthlyStats.map((m) => m.totalRevenue),
              );
              return monthlyStats
                .sort((a, b) => a.month.localeCompare(b.month))
                .map((m) => {
                  const profit = Math.max(0, m.totalRevenue - m.totalCosts);
                  const costW = maxRev > 0 ? (m.totalCosts / maxRev) * 100 : 0;
                  const profitW = maxRev > 0 ? (profit / maxRev) * 100 : 0;
                  return (
                    <div
                      key={m.month}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div style={{ width: "110px" }}>
                        {new Date(m.month + "-01").toLocaleDateString("sk-SK", {
                          year: "numeric",
                          month: "long",
                        })}
                      </div>
                      <div
                        style={{
                          flex: 1,
                          background: "#f1f3f5",
                          height: "14px",
                          borderRadius: "7px",
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            width: `${costW}%`,
                            background: "#dc3545",
                            height: "100%",
                            float: "left",
                          }}
                        ></div>
                        <div
                          style={{
                            width: `${profitW}%`,
                            background: "#28a745",
                            height: "100%",
                            float: "left",
                          }}
                        ></div>
                      </div>
                      <div
                        style={{
                          width: "220px",
                          textAlign: "right",
                          fontSize: "0.9rem",
                        }}
                      >
                        €{m.totalRevenue.toLocaleString("sk-SK")} (náklady €
                        {m.totalCosts.toLocaleString("sk-SK")})
                      </div>
                    </div>
                  );
                });
            })()}
            <div
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                marginTop: "6px",
                fontSize: "0.85rem",
              }}
            >
              <span>Legenda:</span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span
                  style={{
                    width: "16px",
                    height: "10px",
                    background: "#dc3545",
                    display: "inline-block",
                  }}
                ></span>{" "}
                Náklady
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span
                  style={{
                    width: "16px",
                    height: "10px",
                    background: "#28a745",
                    display: "inline-block",
                  }}
                ></span>{" "}
                Zisk
              </span>
            </div>
          </div>
        </div>
      )}

      {/* DETAIL CARDS */}
      <div className="details-grid">
        <div className="detail-card">
          <h3>⏰ Pracovné hodiny</h3>
          <div className="detail-content">
            <p>
              Celkom: <strong>{overview.totalWorkHours.toFixed(0)} h</strong>
            </p>
            <p>
              Pracovné dni:{" "}
              <strong>{overview.weekdayHours.toFixed(0)} h</strong>
            </p>
            <p>
              Víkendy: <strong>{overview.weekendHours.toFixed(0)} h</strong>
            </p>
          </div>
        </div>

        <div className="detail-card">
          <h3>🏗️ Projekty</h3>
          <div className="detail-content">
            <p>
              Celkom: <strong>{projectList.length}</strong>
            </p>
            <p>
              Aktívne:{" "}
              <strong>
                {projectList.filter((p) => p.status === "active").length}
              </strong>
            </p>
            <p>
              S hodinami: <strong>{overview.totalProjects}</strong>
            </p>
          </div>
        </div>

        <div className="detail-card">
          <h3>💰 Príjmy a zisky</h3>
          <div className="detail-content">
            <p>
              Celkové príjmy:{" "}
              <strong>
                €
                {overview.totalRevenue.toLocaleString("sk-SK", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </strong>
            </p>
            <p>
              Celkové náklady:{" "}
              <strong>
                €
                {overview.totalCosts.toLocaleString("sk-SK", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </strong>
            </p>
            <p>
              Čistý zisk:{" "}
              <strong>
                €
                {overview.totalProfit.toLocaleString("sk-SK", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </strong>
            </p>
            <p>
              Firemné výdaje:{" "}
              <strong style={{ color: "#dc3545" }}>
                -€
                {(() => {
                  const totalCompanyExpenses = filteredOwnerTransactions
                    .filter((t) => t.type === "company_expense")
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0);
                  return totalCompanyExpenses.toLocaleString("sk-SK", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                })()}
              </strong>
            </p>
            <p
              style={{
                borderTop: "1px solid #e9ecef",
                paddingTop: "8px",
                marginTop: "8px",
              }}
            >
              Zisk po výdajoch:{" "}
              <strong style={{ color: "#28a745" }}>
                €
                {(() => {
                  const totalCompanyExpenses = filteredOwnerTransactions
                    .filter((t) => t.type === "company_expense")
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0);
                  const profitAfterExpenses =
                    overview.totalProfit - totalCompanyExpenses;
                  return profitAfterExpenses.toLocaleString("sk-SK", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  });
                })()}
              </strong>
            </p>
          </div>
        </div>

        <div className="detail-card">
          <h3>📊 Finančná analýza</h3>
          <div className="detail-content">
            <p>
              Mzdy:{" "}
              <strong>
                €
                {overview.totalSalaries.toLocaleString("sk-SK", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </strong>
            </p>
            <p>
              Ubytovanie:{" "}
              <strong>
                €
                {overview.totalAccommodation.toLocaleString("sk-SK", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </strong>
            </p>
            <p>
              Zisková marža:{" "}
              <strong>{overview.profitMargin.toFixed(1)}%</strong>
            </p>
            <p>
              Efektivita: <strong>{overview.efficiency.toFixed(2)}x</strong>
            </p>
          </div>
        </div>

        {/* OWNER CARDS - Miroslav Pilát */}
        <div className="detail-card owner-card">
          <h3>👤 Miroslav Pilát (Konateľ)</h3>
          <div className="detail-content">
            <p>
              Podiel na zisku (50%):{" "}
              <strong>
                €
                {(() => {
                  // Calculate total company expenses (filtered by selected period)
                  const totalCompanyExpenses = filteredOwnerTransactions
                    .filter((t) => t.type === "company_expense")
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0);
                  // Subtract company expenses from total profit BEFORE splitting
                  const profitAfterExpenses =
                    overview.totalProfit - totalCompanyExpenses;
                  const share = profitAfterExpenses / 2;
                  return share.toLocaleString("sk-SK", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                })()}
              </strong>
            </p>
            <p>
              Výbratá suma:{" "}
              <strong>
                €
                {(() => {
                  const withdrawals = filteredOwnerTransactions
                    .filter(
                      (t) =>
                        t.owner_name === "Miroslav Pilát" &&
                        t.type === "withdrawal",
                    )
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0);
                  return withdrawals.toLocaleString("sk-SK", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                })()}
              </strong>
            </p>
            <p
              style={{
                color: "#28a745",
                fontWeight: "bold",
                fontSize: "1.1rem",
                marginTop: "10px",
                paddingTop: "10px",
                borderTop: "2px solid #e9ecef",
              }}
            >
              Zostáva k výberu:{" "}
              <strong>
                €
                {(() => {
                  // Calculate total company expenses (filtered by selected period)
                  const totalCompanyExpenses = filteredOwnerTransactions
                    .filter((t) => t.type === "company_expense")
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0);
                  // Subtract company expenses from total profit BEFORE splitting
                  const profitAfterExpenses =
                    overview.totalProfit - totalCompanyExpenses;
                  const share = profitAfterExpenses / 2;
                  const withdrawals = filteredOwnerTransactions
                    .filter(
                      (t) =>
                        t.owner_name === "Miroslav Pilát" &&
                        t.type === "withdrawal",
                    )
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0);
                  const remaining = share - withdrawals;
                  return remaining.toLocaleString("sk-SK", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                })()}
              </strong>
            </p>

            <div style={{ marginTop: "15px", display: "flex", gap: "10px" }}>
              <button
                onClick={() =>
                  setShowWithdrawalForm((prev) => ({
                    ...prev,
                    miroslav: !prev.miroslav,
                  }))
                }
                className="btn btn-primary"
              >
                {showWithdrawalForm.miroslav ? "Zavrieť" : "💰 Osobný výber"}
              </button>
              <button
                onClick={() =>
                  setShowExpenseForm((prev) => ({
                    ...prev,
                    miroslav: !prev.miroslav,
                  }))
                }
                className="btn btn-secondary"
              >
                {showExpenseForm.miroslav ? "Zavrieť" : "🧾 Platené na firmu"}
              </button>
            </div>

            {showWithdrawalForm.miroslav && (
              <div
                style={{
                  marginTop: "15px",
                  padding: "15px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                }}
              >
                <h4>Nový výber</h4>
                <input
                  type="number"
                  placeholder="Suma"
                  value={transactionForm.amount}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      amount: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    padding: "8px",
                  }}
                />
                <input
                  type="text"
                  placeholder="Poznámka"
                  value={transactionForm.note}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      note: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    padding: "8px",
                  }}
                />
                <input
                  type="date"
                  value={transactionForm.date}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      date: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    padding: "8px",
                  }}
                />
                <button
                  onClick={() =>
                    handleAddTransaction("Miroslav Pilát", "withdrawal")
                  }
                  className="btn btn-success"
                  style={{ width: "100%" }}
                >
                  Pridať výber
                </button>
              </div>
            )}

            {showExpenseForm.miroslav && (
              <div
                style={{
                  marginTop: "15px",
                  padding: "15px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                }}
              >
                <h4>Nový firemný výdaj</h4>
                <input
                  type="number"
                  placeholder="Suma"
                  value={transactionForm.amount}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      amount: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    padding: "8px",
                  }}
                />
                <input
                  type="text"
                  placeholder="Poznámka"
                  value={transactionForm.note}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      note: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    padding: "8px",
                  }}
                />
                <input
                  type="date"
                  value={transactionForm.date}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      date: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    padding: "8px",
                  }}
                />
                <button
                  onClick={() =>
                    handleAddTransaction("Miroslav Pilát", "company_expense")
                  }
                  className="btn btn-success"
                  style={{ width: "100%" }}
                >
                  Pridať výdaj
                </button>
              </div>
            )}
          </div>
        </div>

        {/* OWNER CARDS - Dominik Andráš */}
        <div className="detail-card owner-card">
          <h3>👤 Dominik Andráš (Konateľ)</h3>
          <div className="detail-content">
            <p>
              Podiel na zisku (50%):{" "}
              <strong>
                €
                {(() => {
                  // Calculate total company expenses (filtered by selected period)
                  const totalCompanyExpenses = filteredOwnerTransactions
                    .filter((t) => t.type === "company_expense")
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0);
                  // Subtract company expenses from total profit BEFORE splitting
                  const profitAfterExpenses =
                    overview.totalProfit - totalCompanyExpenses;
                  const share = profitAfterExpenses / 2;
                  return share.toLocaleString("sk-SK", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                })()}
              </strong>
            </p>
            <p>
              Výbratá suma:{" "}
              <strong>
                €
                {(() => {
                  const withdrawals = filteredOwnerTransactions
                    .filter(
                      (t) =>
                        t.owner_name === "Dominik Andráš" &&
                        t.type === "withdrawal",
                    )
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0);
                  return withdrawals.toLocaleString("sk-SK", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                })()}
              </strong>
            </p>
            <p
              style={{
                color: "#28a745",
                fontWeight: "bold",
                fontSize: "1.1rem",
                marginTop: "10px",
                paddingTop: "10px",
                borderTop: "2px solid #e9ecef",
              }}
            >
              Zostáva k výberu:{" "}
              <strong>
                €
                {(() => {
                  // Calculate total company expenses (filtered by selected period)
                  const totalCompanyExpenses = filteredOwnerTransactions
                    .filter((t) => t.type === "company_expense")
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0);
                  // Subtract company expenses from total profit BEFORE splitting
                  const profitAfterExpenses =
                    overview.totalProfit - totalCompanyExpenses;
                  const share = profitAfterExpenses / 2;
                  const withdrawals = filteredOwnerTransactions
                    .filter(
                      (t) =>
                        t.owner_name === "Dominik Andráš" &&
                        t.type === "withdrawal",
                    )
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0);
                  const remaining = share - withdrawals;
                  return remaining.toLocaleString("sk-SK", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                })()}
              </strong>
            </p>

            <div style={{ marginTop: "15px", display: "flex", gap: "10px" }}>
              <button
                onClick={() =>
                  setShowWithdrawalForm((prev) => ({
                    ...prev,
                    dominik: !prev.dominik,
                  }))
                }
                className="btn btn-primary"
              >
                {showWithdrawalForm.dominik ? "Zavrieť" : "💰 Osobný výber"}
              </button>
              <button
                onClick={() =>
                  setShowExpenseForm((prev) => ({
                    ...prev,
                    dominik: !prev.dominik,
                  }))
                }
                className="btn btn-secondary"
              >
                {showExpenseForm.dominik ? "Zavrieť" : "🧾 Platené na firmu"}
              </button>
            </div>

            {showWithdrawalForm.dominik && (
              <div
                style={{
                  marginTop: "15px",
                  padding: "15px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                }}
              >
                <h4>Nový výber</h4>
                <input
                  type="number"
                  placeholder="Suma"
                  value={transactionForm.amount}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      amount: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    padding: "8px",
                  }}
                />
                <input
                  type="text"
                  placeholder="Poznámka"
                  value={transactionForm.note}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      note: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    padding: "8px",
                  }}
                />
                <input
                  type="date"
                  value={transactionForm.date}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      date: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    padding: "8px",
                  }}
                />
                <button
                  onClick={() =>
                    handleAddTransaction("Dominik Andráš", "withdrawal")
                  }
                  className="btn btn-success"
                  style={{ width: "100%" }}
                >
                  Pridať výber
                </button>
              </div>
            )}

            {showExpenseForm.dominik && (
              <div
                style={{
                  marginTop: "15px",
                  padding: "15px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                }}
              >
                <h4>Nový firemný výdaj</h4>
                <input
                  type="number"
                  placeholder="Suma"
                  value={transactionForm.amount}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      amount: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    padding: "8px",
                  }}
                />
                <input
                  type="text"
                  placeholder="Poznámka"
                  value={transactionForm.note}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      note: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    padding: "8px",
                  }}
                />
                <input
                  type="date"
                  value={transactionForm.date}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      date: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    marginBottom: "10px",
                    padding: "8px",
                  }}
                />
                <button
                  onClick={() =>
                    handleAddTransaction("Dominik Andráš", "company_expense")
                  }
                  className="btn btn-success"
                  style={{ width: "100%" }}
                >
                  Pridať výdaj
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MONTHLY STATS */}
      {monthlyStats.length > 0 && (
        <div className="monthly-stats">
          <h2>📊 Mesačné štatistiky ziskovosti</h2>
          <div className="monthly-table">
            <table>
              <thead>
                <tr>
                  <th>Mesiac</th>
                  <th>Príjmy</th>
                  <th>Mzdy</th>
                  <th>Ubytovanie</th>
                  <th>Náklady</th>
                  <th>Zisk</th>
                  <th>Marža</th>
                  <th>Hodiny</th>
                </tr>
              </thead>
              <tbody>
                {monthlyStats
                  .slice(-12)
                  .reverse()
                  .map((month) => (
                    <tr key={month.month}>
                      <td>
                        <strong>{month.month}</strong>
                      </td>
                      <td>
                        €
                        {month.totalRevenue.toLocaleString("sk-SK", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td>
                        €
                        {month.totalSalaries.toLocaleString("sk-SK", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td>
                        €
                        {month.totalAccommodation.toLocaleString("sk-SK", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td>
                        €
                        {month.totalCosts.toLocaleString("sk-SK", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td
                        className={
                          month.profit > 0
                            ? "profit-positive"
                            : "profit-negative"
                        }
                      >
                        €
                        {month.profit.toLocaleString("sk-SK", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td>{month.profitMargin.toFixed(1)}%</td>
                      <td>{month.totalHours.toFixed(0)} h</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* OWNER TRANSACTIONS TABLE */}
      {ownerTransactions.length > 0 && (
        <div className="detail-card" style={{ marginTop: "2rem" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <h3>💼 Výdaje firemné</h3>
            {selectedTransactions.length > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="btn btn-danger"
                style={{ padding: "10px 20px" }}
              >
                🗑️ Vymazať označené ({selectedTransactions.length})
              </button>
            )}
          </div>
          <div
            className="expenses-columns"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "20px",
            }}
          >
            {/* Firemné výdaje */}
            <div className="expense-column-inner">
              <h3>🏢 Firemné výdaje</h3>
              <div
                className="expense-list"
                style={{ maxHeight: "500px", overflowY: "auto" }}
              >
                {filteredOwnerTransactions
                  .filter((t) => t.type === "company_expense")
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((t) => (
                    <div
                      key={t.id}
                      className="expense-item"
                      style={{
                        padding: "10px",
                        borderBottom: "1px solid #eee",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTransactions.includes(t.id)}
                        onChange={() => handleToggleTransaction(t.id)}
                        style={{ cursor: "pointer" }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "bold" }}>
                          €
                          {parseFloat(t.amount).toLocaleString("sk-SK", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {t.note}
                        </div>
                        <div style={{ fontSize: "11px", color: "#999" }}>
                          {new Date(t.date).toLocaleDateString("sk-SK")} -{" "}
                          {t.owner_name}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteTransaction(t.id)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#dc3545",
                          cursor: "pointer",
                          fontSize: "18px",
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                <div
                  style={{
                    padding: "10px",
                    fontWeight: "bold",
                    borderTop: "2px solid #333",
                  }}
                >
                  Spolu: €
                  {filteredOwnerTransactions
                    .filter((t) => t.type === "company_expense")
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0)
                    .toLocaleString("sk-SK", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                </div>
              </div>
            </div>

            {/* Miroslav Pilát výbery */}
            <div className="expense-column-inner">
              <h3>👤 Miroslav Pilát - Výbery</h3>
              <div
                className="expense-list"
                style={{ maxHeight: "500px", overflowY: "auto" }}
              >
                {filteredOwnerTransactions
                  .filter(
                    (t) =>
                      t.owner_name === "Miroslav Pilát" &&
                      t.type === "withdrawal",
                  )
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((t) => (
                    <div
                      key={t.id}
                      className="expense-item"
                      style={{
                        padding: "10px",
                        borderBottom: "1px solid #eee",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTransactions.includes(t.id)}
                        onChange={() => handleToggleTransaction(t.id)}
                        style={{ cursor: "pointer" }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "bold" }}>
                          €
                          {parseFloat(t.amount).toLocaleString("sk-SK", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {t.note}
                        </div>
                        <div style={{ fontSize: "11px", color: "#999" }}>
                          {new Date(t.date).toLocaleDateString("sk-SK")}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteTransaction(t.id)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#dc3545",
                          cursor: "pointer",
                          fontSize: "18px",
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                <div
                  style={{
                    padding: "10px",
                    fontWeight: "bold",
                    borderTop: "2px solid #333",
                  }}
                >
                  Spolu: €
                  {filteredOwnerTransactions
                    .filter(
                      (t) =>
                        t.owner_name === "Miroslav Pilát" &&
                        t.type === "withdrawal",
                    )
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0)
                    .toLocaleString("sk-SK", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                </div>
              </div>
            </div>

            {/* Dominik Andráš výbery */}
            <div className="expense-column-inner">
              <h3>👤 Dominik Andráš - Výbery</h3>
              <div
                className="expense-list"
                style={{ maxHeight: "500px", overflowY: "auto" }}
              >
                {filteredOwnerTransactions
                  .filter(
                    (t) =>
                      t.owner_name === "Dominik Andráš" &&
                      t.type === "withdrawal",
                  )
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((t) => (
                    <div
                      key={t.id}
                      className="expense-item"
                      style={{
                        padding: "10px",
                        borderBottom: "1px solid #eee",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTransactions.includes(t.id)}
                        onChange={() => handleToggleTransaction(t.id)}
                        style={{ cursor: "pointer" }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "bold" }}>
                          €
                          {parseFloat(t.amount).toLocaleString("sk-SK", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {t.note}
                        </div>
                        <div style={{ fontSize: "11px", color: "#999" }}>
                          {new Date(t.date).toLocaleDateString("sk-SK")}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteTransaction(t.id)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#dc3545",
                          cursor: "pointer",
                          fontSize: "18px",
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                <div
                  style={{
                    padding: "10px",
                    fontWeight: "bold",
                    borderTop: "2px solid #333",
                  }}
                >
                  Spolu: €
                  {filteredOwnerTransactions
                    .filter(
                      (t) =>
                        t.owner_name === "Dominik Andráš" &&
                        t.type === "withdrawal",
                    )
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0)
                    .toLocaleString("sk-SK", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TOP EMPLOYEES - ALL USERS */}
      {employees.length > 0 && (
        <div className="top-performers">
          <h2>🏆 Top zamestnanci (všetci)</h2>
          <div
            className="performers-table"
            style={{ maxHeight: "500px", overflowY: "auto" }}
          >
            <table>
              <thead>
                <tr>
                  <th>Zamestnanec</th>
                  <th>Hodiny</th>
                  <th>Dni</th>
                  <th>Mzda</th>
                  <th>Ubytovanie</th>
                  <th>Príjem firmy</th>
                  <th>Zisk</th>
                  <th>Marža</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr
                    key={emp.id}
                    onClick={() => handleEmployeeClick(emp)}
                    style={{ cursor: "pointer" }}
                    className="employee-row-clickable"
                  >
                    <td>
                      <strong>{emp.name}</strong>
                    </td>
                    <td>{emp.totalHours.toFixed(0)} h</td>
                    <td>{emp.totalDays}</td>
                    <td>
                      €
                      {emp.totalSalaryCost.toLocaleString("sk-SK", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </td>
                    <td>
                      €
                      {emp.accommodationCost.toLocaleString("sk-SK", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </td>
                    <td>
                      €
                      {emp.totalRevenue.toLocaleString("sk-SK", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </td>
                    <td className="profit-positive">
                      €
                      {emp.profit.toLocaleString("sk-SK", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </td>
                    <td>{emp.profitMargin.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PROJECT STATS */}
      {projectList.filter((p) => p.totalHours > 0).length > 0 && (
        <div className="project-stats">
          <h2>🏗️ Štatistiky projektov</h2>
          <div className="projects-table">
            <table>
              <thead>
                <tr>
                  <th>Projekt</th>
                  <th>Hodiny</th>
                  <th>Dni</th>
                  <th>Ľudia</th>
                  <th>Mzdy</th>
                  <th>Ubytovanie</th>
                  <th>Príjmy</th>
                  <th>Zisk</th>
                  <th>Marža</th>
                </tr>
              </thead>
              <tbody>
                {projectList
                  .filter((p) => p.totalHours > 0)
                  .map((proj) => (
                    <tr key={proj.id}>
                      <td>
                        <strong>{proj.name}</strong>
                      </td>
                      <td>{proj.totalHours.toFixed(0)} h</td>
                      <td>{proj.totalDays}</td>
                      <td>{proj.uniqueEmployees}</td>
                      <td>
                        €
                        {proj.totalSalaryCost.toLocaleString("sk-SK", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td>
                        €
                        {proj.accommodationCost.toLocaleString("sk-SK", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td>
                        €
                        {proj.totalRevenue.toLocaleString("sk-SK", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td
                        className={
                          proj.profit > 0
                            ? "profit-positive"
                            : "profit-negative"
                        }
                      >
                        €
                        {proj.profit.toLocaleString("sk-SK", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td>{proj.profitMargin.toFixed(1)}%</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* EMPLOYEE DETAIL MODAL */}
      {showEmployeeDetailModal && selectedEmployee && (
        <div
          className="modal-overlay"
          onClick={() => setShowEmployeeDetailModal(false)}
        >
          <div
            className="modal-content employee-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>📊 Detailný rozpis pre: {selectedEmployee.name}</h2>
              <button
                className="close-button"
                onClick={() => setShowEmployeeDetailModal(false)}
              >
                ×
              </button>
            </div>

            <div
              className="modal-body"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              {/* Celkový súhrn */}
              <div
                className="employee-summary"
                style={{
                  padding: "15px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "10px",
                }}
              >
                <div>
                  <strong>Celkové hodiny:</strong>{" "}
                  {selectedEmployee.totalHours.toFixed(2)} h
                </div>
                <div>
                  <strong>Celkové dni:</strong> {selectedEmployee.totalDays}
                </div>
                <div>
                  <strong>Mzda celkom:</strong> €
                  {selectedEmployee.totalSalaryCost.toLocaleString("sk-SK", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
                <div>
                  <strong>Príjem firmy:</strong> €
                  {selectedEmployee.totalRevenue.toLocaleString("sk-SK", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
              </div>

              {/* Mesačný rozpis */}
              {employeeMonthlyDetails.map((month) => (
                <div
                  key={month.monthKey}
                  className="month-breakdown"
                  style={{
                    marginBottom: "30px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "15px",
                    backgroundColor: "#fff",
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 15px 0",
                      padding: "10px",
                      backgroundColor: "#007bff",
                      color: "white",
                      borderRadius: "5px",
                    }}
                  >
                    📅 {month.monthName}
                  </h3>

                  {/* Mesačný súhrn */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: "10px",
                      marginBottom: "15px",
                      padding: "10px",
                      backgroundColor: "#f9f9f9",
                      borderRadius: "5px",
                    }}
                  >
                    <div>
                      <strong>Dni na projekte (work):</strong> {month.workDays}
                    </div>
                    <div>
                      <strong>Dni doma (home):</strong> {month.homeDays}
                    </div>
                    <div>
                      <strong>Víkendové dni:</strong> {month.weekendDays}
                    </div>
                    <div>
                      <strong>Pracovné dni:</strong> {month.weekdayDays}
                    </div>
                    <div>
                      <strong>Celkom hodín:</strong>{" "}
                      {month.totalHours.toFixed(2)} h
                    </div>
                    <div>
                      <strong>Mzda:</strong> €
                      {month.totalSalary.toLocaleString("sk-SK", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </div>
                  </div>

                  {/* Rozpis podľa projektov */}
                  <div style={{ marginTop: "15px" }}>
                    <h4 style={{ marginBottom: "10px", color: "#555" }}>
                      🏗️ Rozpis podľa projektov:
                    </h4>
                    {Object.entries(month.projects).map(
                      ([projectName, projectData]) => (
                        <div
                          key={projectName}
                          style={{
                            padding: "10px",
                            backgroundColor: "#f0f8ff",
                            borderLeft: "4px solid #007bff",
                            marginBottom: "10px",
                            borderRadius: "4px",
                          }}
                        >
                          <div
                            style={{ fontWeight: "bold", marginBottom: "8px" }}
                          >
                            {projectName}
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "8px",
                              fontSize: "14px",
                            }}
                          >
                            {/* Pracovné dni */}
                            {projectData.weekdayDays > 0 && (
                              <div
                                style={{
                                  padding: "8px",
                                  backgroundColor: "white",
                                  borderRadius: "4px",
                                }}
                              >
                                <div
                                  style={{ color: "#666", fontSize: "12px" }}
                                >
                                  Pracovné dni:
                                </div>
                                <div style={{ fontWeight: "bold" }}>
                                  {projectData.weekdayDays} dní ×{" "}
                                  {(
                                    projectData.weekdayHours /
                                    projectData.weekdayDays
                                  ).toFixed(1)}{" "}
                                  h/deň = {projectData.weekdayHours.toFixed(1)}{" "}
                                  h celkom
                                </div>
                                <div
                                  style={{ color: "#007bff", marginTop: "4px" }}
                                >
                                  {projectData.weekdayHours.toFixed(1)} h × €
                                  {projectData.weekdayRate}= €
                                  {(
                                    projectData.weekdayHours *
                                    projectData.weekdayRate
                                  ).toFixed(2)}
                                </div>
                              </div>
                            )}

                            {/* Víkendové dni */}
                            {projectData.weekendDays > 0 && (
                              <div
                                style={{
                                  padding: "8px",
                                  backgroundColor: "white",
                                  borderRadius: "4px",
                                }}
                              >
                                <div
                                  style={{ color: "#666", fontSize: "12px" }}
                                >
                                  Víkendové dni:
                                </div>
                                <div style={{ fontWeight: "bold" }}>
                                  {projectData.weekendDays} dní ×{" "}
                                  {(
                                    projectData.weekendHours /
                                    projectData.weekendDays
                                  ).toFixed(1)}{" "}
                                  h/deň = {projectData.weekendHours.toFixed(1)}{" "}
                                  h celkom
                                </div>
                                <div
                                  style={{ color: "#28a745", marginTop: "4px" }}
                                >
                                  {projectData.weekendHours.toFixed(1)} h × €
                                  {projectData.weekendRate}= €
                                  {(
                                    projectData.weekendHours *
                                    projectData.weekendRate
                                  ).toFixed(2)}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </div>

                  {/* Celkový výpočet pre mesiac */}
                  <div
                    style={{
                      marginTop: "15px",
                      padding: "15px",
                      backgroundColor: "#e7f3ff",
                      borderRadius: "5px",
                      border: "2px solid #007bff",
                    }}
                  >
                    <h4 style={{ margin: "0 0 10px 0", color: "#007bff" }}>
                      💰 Celkový výpočet:
                    </h4>
                    <div style={{ fontSize: "14px", lineHeight: "1.8" }}>
                      <div>
                        <strong>Pracovné hodiny:</strong>{" "}
                        {month.weekdayHours.toFixed(2)} h
                      </div>
                      <div>
                        <strong>Víkendové hodiny:</strong>{" "}
                        {month.weekendHours.toFixed(2)} h
                      </div>
                      <div
                        style={{
                          marginTop: "10px",
                          paddingTop: "10px",
                          borderTop: "1px solid #007bff",
                          fontWeight: "bold",
                          fontSize: "16px",
                        }}
                      >
                        Celková mzda: €
                        {month.totalSalary.toLocaleString("sk-SK", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {employeeMonthlyDetails.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "30px",
                    color: "#999",
                  }}
                >
                  Žiadne dáta pre zobrazenie
                </div>
              )}
            </div>

            <div
              className="modal-footer"
              style={{
                padding: "15px",
                borderTop: "1px solid #ddd",
                textAlign: "right",
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => setShowEmployeeDetailModal(false)}
              >
                Zavrieť
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {showSuccessNotification && (
        <div className="success-notification">
          <div className="success-notification-content">
            <div className="success-icon">✓</div>
            <div className="success-message">{notificationMessage}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsImproved;
